/**
 * Knock Chatbot Block
 *
 * Injection block that loads the Knock Doorway widget (floating chat + tour
 * scheduler launcher). All the load logic — script injection, init/inject,
 * health checks, and self-healing retries — lives in the framework-agnostic
 * `loader` so it can be unit-tested and so the widget survives React
 * remounts (SPA navigation, StrictMode). The component is just a thin,
 * idempotent trigger: it asks the loader to ensure the widget exists and
 * deliberately does NOT tear anything down on unmount. Once the launcher is
 * attached it stays attached.
 *
 * See loader.ts for why the old "remove + re-add the script on every mount"
 * approach was unreliable (flaky Doorway API + destructive remounts).
 */

import { useEffect } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { useBlockToken } from '@vibesites/runtime/react';
import type { BlockDefinition, BlockProps } from '../../types';
import { ensureKnockDoorway } from './loader';

function normalize(value: string | null | undefined) {
  if (!value) return null;
  const trimmed = value.trim();
  return trimmed || null;
}

function KnockChatbotComponent(_props: BlockProps) {
  const apiToken = normalize(
    useBlockToken('api_token', { defaultField: 'custom_knock_company_id' }),
  );
  const communityId = normalize(
    useBlockToken('community_id', { defaultField: 'custom_knock_community_id' }),
  );

  useEffect(() => {
    if (!apiToken || !communityId) return;
    // Idempotent: collapses repeat mounts to a single managed load and
    // leaves the widget attached on unmount.
    ensureKnockDoorway(apiToken, communityId);
  }, [apiToken, communityId]);

  return null;
}

const contentSchema: ComponentContentSchema = {
  slot: 'KnockChatbot',
  displayName: 'Knock Chatbot',
  tokens: {
    api_token: {
      label: 'Knock API Token',
      type: 'text',
      defaultField: 'custom_knock_company_id',
      suggestedFields: ['custom_knock_company_id'],
      description:
        'Knock Doorway embed API token. Typically the same across all communities in a company — maps to custom_knock_company_id by default.',
      required: true,
    },
    community_id: {
      label: 'Knock Community ID',
      type: 'text',
      defaultField: 'custom_knock_community_id',
      suggestedFields: ['custom_knock_community_id'],
      description: 'Per-property Knock community ID used to target this site.',
      required: true,
    },
  },
};

export const knockChatbotBlock: BlockDefinition = {
  id: 'knock-chatbot',
  name: 'Knock Chatbot',
  description:
    'Floating Knock Doorway chat + tour scheduling widget. The launcher (bottom-right bubble) is the UI — clicking it opens the chat flow where users can schedule tours or message leasing.',
  category: 'Integrations',
  placement: 'injection',
  defaultScope: 'template',
  component: KnockChatbotComponent,
  contentSchema,
  tags: [
    'knock',
    'doorway',
    'chatbot',
    'tour',
    'scheduling',
    'leasing',
    'chat',
    'widget',
  ],
};
